<template>
<b-modal
    id="merge-ticket-modal"
    title="Merge Tickets"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    centered
  >
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="container-fluid px-3 d-flex">
        <div class="flex-grow-1">
          <div class="btn-group mr-2">
            <button
              type="button"
              class="btn"
              :class="{ 'btn-itg-dark': currentStep === 1, 'btn-itg': currentStep !== 1 }"
              @click="goToStep(1)"
              :disabled="isSaving"
            >
              <i class="fa fa-search mr-2"></i>
              Search Ticket
            </button>
            <button
              type="button"
              class="btn"
              :class="{ 'btn-itg-dark': currentStep === 2, 'btn-itg': currentStep !== 2 }"
              @click="goToStep(2)"
              :disabled="currentStep === 1 || isSaving"
            >
              <i class="fa fa-cogs mr-2"></i>
              Properties
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Body -->
    <div class="merge-ticket-form px-4 pt-4 pb-0 w-100" style="border-radius:0;">
      <!-- Step 1: Search for ticket to merge with -->
      <div v-if="currentStep === 1" class="step-content">
        <h5 class="mb-3 font-weight-bold">Search for Ticket to Merge With</h5>
        <div class="form-group">
          <label for="search-query">Search Tickets</label>
          <input
            id="search-query"
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="Enter ticket ID, subject, or description to search..."
          />
        </div>

        <div v-if="isSearching" class="text-center py-4">
          <b-spinner></b-spinner>
          <p class="mt-2">Searching...</p>
        </div>

        <div v-else-if="searchResults && searchResults.length > 0" class="search-results">
          <h6 class="mb-3">Search Results</h6>
          <div class="ticket-list">
            <selectable-ticket-card
              v-for="ticket in searchResults"
              :key="ticket.id"
              :ticket="ticket"
              :selected="selectedTicket && selectedTicket.id === ticket.id"
              @select="selectTicket"
              class="mb-2"
            />
          </div>
        </div>

        <div v-else-if="searchQuery && !isSearching" class="text-center py-4">
          <p class="text-muted">No tickets found matching your search.</p>
        </div>
      </div>

      <!-- Step 2: Property Selection -->
      <div v-if="currentStep === 2" class="step-content">
        <h5 class="mb-3 font-weight-bold">Select Properties for Merged Ticket</h5>
        <p class="text-muted mb-4">Choose which ticket's properties to keep in the merged ticket.</p>
        <div class="table-responsive">
          <table class="table table-bordered table-striped">
            <thead>
              <tr>
                <th style="width: 30%">Property</th>
                <th style="width: 70%">Value to Use</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="property in ticketProperties" :key="property.key">
                <td><strong>{{ property.label }}</strong></td>
                <td>
                  <div class="d-flex align-items-center">
                    <select class="form-control mr-3" style="max-width: 440px; display: inline-block;" v-model="form.property_selections[property.key]">
                      <option :value="1">Current Ticket: {{ getPreviewValue(property.key, 1) }}</option>
                      <option :value="2">Selected Ticket: {{ getPreviewValue(property.key, 2) }}</option>
                    </select>
                    <span class="badge badge-primary" v-if="form.property_selections[property.key] === 1">{{ getPreviewValue(property.key, 1) }}</span>
                    <span class="badge badge-success" v-else>{{ getPreviewValue(property.key, 2) }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end align-items-center">
        <div class="d-flex gap-2">
          <button 
            type="button" 
            class="btn btn-itg" 
            @click="previousStep"
            :disabled="currentStep === 1 || isSaving"
          >
            <i class="fa fa-chevron-left mr-1"></i>
            Previous
          </button>

          <button
            type="button"
            class="btn btn-itg"
            @click="nextStep"
            :disabled="!canProceedToNextStep || isSaving"
            v-if="currentStep === 1"
          >
            Next
            <i class="fa fa-chevron-right ml-1"></i>
          </button>

          <button
            type="button"
            class="btn btn-itg-dark"
            @click="mergeTickets"
            :disabled="isSaving || currentStep !== 2"
            v-if="currentStep === 2"
          >
            <span v-if="isSaving">
              <i class="fa fa-spinner fa-spin mr-1"></i>
              Merging...
            </span>
            <span v-else> Merge Tickets </span>
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Forms from "../../../../../mixins/Forms";
import Dates from "../../../../../mixins/Dates";
import Tickets from "../../../../../mixins/Tickets";
import _ from 'lodash';
import SelectableTicketCard from "./SelectableTicketCard.vue";

export default {
  name: "MergeTicketModal",
  components: { SelectableTicketCard },
  mixins: [Forms, Dates, Tickets],
  data() {
    return {
      currentStep: 1,
      isSaving: false,
      isSearching: false,
      searchQuery: '',
      searchResults: [],
      selectedTicket: null,
      currentTicketContacts: [],
      selectedTicketContacts: [],
      form: {
        ticket_id_1: null,
        ticket_id_2: null,
        property_selections: {
          axum_booking_id: 1,
          subject: 1,
          description: 1,
          source: 1,
          difficulty: 1,
          status: 1,
          priority: 1,
          agent_id: 1,
          primary_contact_id: 1,
          caused_by_itg_error: 1
        }
      }
    };
  },
  computed: {
    ...mapGetters('ticketViewer', {
      currentTicket: 'ticket'
    }),

    canProceedToNextStep() {
      switch (this.currentStep) {
        case 1:
          return this.selectedTicket !== null;
        case 2:
          return true; // All properties have default selections
        case 3:
          return true; // Review step
        default:
          return false;
      }
    },

    ticketProperties() {
      return [
        { key: 'axum_booking_id', label: 'Axum Booking ID' },
        { key: 'subject', label: 'Subject' },
        { key: 'description', label: 'Description' },
        { key: 'source', label: 'Source' },
        { key: 'difficulty', label: 'Difficulty' },
        { key: 'status', label: 'Status' },
        { key: 'priority', label: 'Priority' },
        { key: 'agent_id', label: 'Agent ID' },
        { key: 'primary_contact_id', label: 'Primary Contact' },
        { key: 'caused_by_itg_error', label: 'Caused by ITG Error' }
      ];
    }
  },
  mounted() {
    // Listen for modal show event
    this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
      if (modalId === 'merge-ticket-modal') {
        this.resetForm();
        this.loadCurrentTicketContacts();
      }
    });
  },
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('bv::modal::shown');
  },
  watch: {
    searchQuery() {
      this.searchTickets();
    },
    selectedTicket(newVal) {
      if (newVal) {
        this.loadSelectedTicketContacts();
      }
    }
  },
  methods: {
    goToStep(step) {
      if (step >= 1 && step <= 3) {
        // Validate current step before allowing navigation
        if (step > this.currentStep && !this.canProceedToNextStep) {
          this.$notify.error({
            title: 'Validation Error',
            message: 'Please complete the current step before proceeding'
          });
          return;
        }
        
        this.currentStep = step;
      }
    },

    nextStep() {
      if (this.currentStep < 3) {
        if (!this.canProceedToNextStep) {
          this.$notify.error({
            title: 'Validation Error',
            message: 'Please complete all required fields before proceeding'
          });
          return;
        }
        
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    searchTickets: _.debounce(function() {
      if (!this.searchQuery.trim()) {
        this.searchResults = [];
        return;
      }

      this.isSearching = true;
      
      axios.post(API_BASE + '/tickets/search', {
        q: this.searchQuery,
        results_per_page: 10
      }).then(response => {
        this.isSearching = false;
        // Filter out the current ticket from results on frontend
        this.searchResults = (response.data.data || []).filter(ticket => ticket.id !== this.currentTicket.id);
      }).catch(error => {
        this.isSearching = false;
        console.error('Error searching tickets:', error);
        this.$notify.error({
          title: 'Error',
          message: 'Failed to search tickets'
        });
      });
    }, 500),

    selectTicket(ticket) {
      this.selectedTicket = ticket;
      this.form.ticket_id_1 = this.currentTicket.id;
      this.form.ticket_id_2 = ticket.id;
    },

    selectProperty(propertyKey, ticketNumber) {
      this.form.property_selections[propertyKey] = ticketNumber;
    },

    getSelectedPropertyValue(propertyKey) {
      const selection = this.form.property_selections[propertyKey];
      const sourceTicket = selection === 1 ? this.currentTicket : this.selectedTicket;
      let value = sourceTicket[propertyKey];
      if (propertyKey === 'status') {
        value = this.getHumanReadableTicketStatus(value);
      } else if (propertyKey === 'priority') {
        value = this.getHumanReadableTicketPriority(value);
      } else if (propertyKey === 'difficulty') {
        value = this.getHumanReadableTicketDifficulty(value);
      } else if (propertyKey === 'source') {
        value = this.getHumanReadableTicketSource(value);
      } else if (propertyKey === 'primary_contact_id') {
        const contacts = selection === 1 ? this.currentTicketContacts : this.selectedTicketContacts;
        let contact = contacts.find(c => c.id == value);
        if (!contact && sourceTicket.primary_contact) {
          contact = sourceTicket.primary_contact;
        }
        value = contact ? `${contact.first_name} ${contact.last_name}` : '-';
      }
      return value || '-';
    },
    getPreviewValue(propertyKey, ticketNumber) {
      const sourceTicket = ticketNumber === 1 ? this.currentTicket : this.selectedTicket;
      let value = sourceTicket[propertyKey];
      if (propertyKey === 'status') {
        value = this.getHumanReadableTicketStatus(value);
      } else if (propertyKey === 'priority') {
        value = this.getHumanReadableTicketPriority(value);
      } else if (propertyKey === 'difficulty') {
        value = this.getHumanReadableTicketDifficulty(value);
      } else if (propertyKey === 'source') {
        value = this.getHumanReadableTicketSource(value);
      } else if (propertyKey === 'primary_contact_id') {
        const contacts = ticketNumber === 1 ? this.currentTicketContacts : this.selectedTicketContacts;
        let contact = contacts.find(c => c.id == value);
        if (!contact && sourceTicket.primary_contact) {
          contact = sourceTicket.primary_contact;
        }
        value = contact ? `${contact.first_name} ${contact.last_name}` : '-';
      }
      return value || '-';
    },

    async mergeTickets() {
      if (this.isSaving) return;

      this.isSaving = true;

      try {
        // Flatten the payload: spread property_selections into the top-level request
        const payload = {
          ticket_id_1: this.form.ticket_id_1,
          ticket_id_2: this.form.ticket_id_2,
          ...this.form.property_selections
        };

        const response = await axios.post(
          `${API_BASE}/tickets/merge`,
          payload
        );

        this.$bvModal.hide('merge-ticket-modal');
        
        this.$notify.success({
          title: 'Success!',
          message: 'Tickets merged successfully'
        });

        // Reload the current ticket to show updated data
        this.$store.dispatch('ticketViewer/reloadTicket');
      } catch (error) {
        this.isSaving = false;
        if (error.response && error.response.data && error.response.data.errors) {
          this.$notify.error({
            title: 'Error',
            message: error.response.data.message || 'Failed to merge tickets'
          });
        } else {
          this.notifyErrors(error);
        }
      }
    },

    resetForm() {
      this.currentStep = 1;
      this.searchQuery = '';
      this.searchResults = [];
      this.selectedTicket = null;
      this.form = {
        ticket_id_1: null,
        ticket_id_2: null,
        property_selections: {
          axum_booking_id: 1,
          subject: 1,
          description: 1,
          source: 1,
          difficulty: 1,
          status: 1,
          priority: 1,
          agent_id: 1,
          primary_contact_id: 1,
          caused_by_itg_error: 1
        }
      };
      this.isSaving = false;
    },
    async loadCurrentTicketContacts() {
      if (!this.currentTicket || !this.currentTicket.id) return;
      try {
        const response = await axios.get(`${API_BASE}/tickets/${this.currentTicket.id}/contacts`);
        this.currentTicketContacts = response.data;
      } catch (e) {
        this.currentTicketContacts = [];
      }
    },
    async loadSelectedTicketContacts() {
      if (!this.selectedTicket || !this.selectedTicket.id) return;
      try {
        const response = await axios.get(`${API_BASE}/tickets/${this.selectedTicket.id}/contacts`);
        this.selectedTicketContacts = response.data;
      } catch (e) {
        this.selectedTicketContacts = [];
      }
    }
  }
};
</script>

<style lang="scss">
#merge-ticket-modal {
  .merge-ticket-form {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    
    .form-group {
      label {
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
      }

      .form-control {
        border-radius: 0.5rem;
        border: 1px solid #ced4da;
        padding: 0.75rem 1rem;
        height: auto;
        transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

        &:focus {
          border-color: #2a3042;
          box-shadow: 0 0 0 0.2rem rgba(42, 48, 66, 0.25);
        }
      }
    }
  }

  .toolbar {
    border-top: 1px #ebeff3 solid !important;
    border-bottom: 1px #cfd7df solid !important;
    padding-top: 11px;
    padding-bottom: 11px;
    box-shadow: 0 2px 4px 0 rgba(24, 50, 71, 0.08);
    z-index: 1;

    .btn-group {
      .btn {
        border-radius: 0.375rem;
        margin-right: 0.5rem;

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  .modal-dialog {
    max-width: 100vw;
    width: calc(100vw);
    margin: auto;
    transform: none;

    .modal-body {
      padding: 0;
      display: flex;
      flex-direction: column;
      height: 100%;
      max-height: calc(100vh - 127px);
    }

    .modal-header {
      border-bottom: none;
      background: #2a3042;
      padding-top: 1rem;
      padding-bottom: 1rem;
      padding-left: 1.2rem;
      padding-right: 1.2rem;

      .modal-title {
        color: #fff !important;
      }

      .close {
        filter: invert(1);
        padding-top: 18px;
        padding-right: 20px;
      }
    }

    .modal-content {
      border: 0;
      min-height: calc(100vh - 300px);
      height: calc(100vh);
      max-height: calc(100vh);
    }
  }

  .modal-footer {
    border-top: 1px solid #dee2e6;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .modal-footer .btn {
    min-width: 100px;
  }

  .ticket-item {
    transition: all 0.2s ease;
    
    &:hover {
      .card {
        box-shadow: 0 0.75rem 1.5rem rgb(18 38 63 / 15%) !important;
      }
    }
    
    &.selected {
      .card {
        border: 2px solid #2196f3 !important;
        box-shadow: 0 0.75rem 1.5rem rgb(33 150 243 / 20%) !important;
      }
    }
  }

  .property-selection {
    .property-group {
      margin-bottom: 1.5rem;
    }
    
    .ticket-property-value {
      word-break: break-word;
    }
  }

  .review-summary {
    .merged-properties {
      background-color: #f8f9fa;
      padding: 1rem;
      border-radius: 0.5rem;
    }
  }

  .gap-2 {
    gap: 0.5rem;
  }
}
</style> 